import { c as _c } from "react-compiler-runtime";
import { createPortal } from 'react-dom';
import styled from 'foundations-theming/utils';
import { CopyIcon } from 'foundations-assets/icons/components/CopyIcon';
import I18n from 'I18n';
import { useCellSelectionSelector, useWidthsSelector } from '../../redux/reduxHooks';
import { getWidths } from '../../resizing/redux/widthsSlice';
import { useCellSelectionConfig, useCopySelectionRef, useTrackCellSelection } from '../context/CellSelectionContext';
import { isCellSelectionEnabled } from '../config';
import { getHasSelection, getIsDragging, getActiveRangeBottomRightCoord, getSelectedCellCoords } from '../redux/cellSelectionSlice';
import { getSelectedCells } from '../utils/getSelectedCells';
import { getCopyShortcutLabel } from '../utils/getCopyShortcutLabel';
import { useFillHandleAnchorElement } from '../hooks/useFillHandleAnchorElement';
import { useIsAnchorCellEditing } from '../hooks/useIsAnchorCellEditing';
import { CellActionMenu } from './CellActionMenu';
import { jsx as _jsx } from "react/jsx-runtime";
// Cells from the table edge that flip the menu to the inside placement.
const EDGE_THRESHOLD = 1;
const Overlay = styled.div.withConfig({
  displayName: "CellActionMenuOverlay__Overlay"
})(["position:absolute;z-index:2;", ""], ({
  $inside,
  theme
}) => $inside ?
// center-right INSIDE the fill-handle cell (edge fallback)
`
        top: 50%;
        right: ${theme.space['100']};
        transform: translateY(-50%);
      ` :
// just OUTSIDE the bottom-right corner, straddling it vertically
`
        top: 100%;
        left: 100%;
        transform: translate(${theme.space['100']}, -50%);
      `);

/**
 * Renders the cell-selection action menu anchored just outside the active
 * selection's fill-handle cell, at its bottom-right corner. Always offers a
 * built-in Copy action, followed by any consumer-provided actions from the
 * cell-selection config.
 *
 * Falls back to center-right *inside* the cell when the fill-handle cell is in
 * the table's last row or column, so the menu never renders off-table.
 *
 * The menu is portaled into the fill-handle `<td>` and positioned with CSS, so
 * it scrolls with the cell natively — no coordinate math required. It is hidden
 * while a drag is in progress, while the fill-handle cell is not in the DOM
 * (unmounted by virtualization), and while that cell is in edit mode (the menu
 * would sit on top of the consumer's editor).
 */
export const CellActionMenuOverlay = t0 => {
  const $ = _c(28);
  const {
    scrollContainerElementRef,
    columns,
    rows,
    getRowId
  } = t0;
  const config = useCellSelectionConfig();
  const copySelectionRef = useCopySelectionRef();
  const track = useTrackCellSelection();
  const hasSelection = useCellSelectionSelector(getHasSelection);
  const isDragging = useCellSelectionSelector(getIsDragging);
  const bottomRightCoord = useCellSelectionSelector(getActiveRangeBottomRightCoord);
  const coords = useCellSelectionSelector(getSelectedCellCoords);
  const widths = useWidthsSelector(getWidths);
  let t1;
  if ($[0] !== bottomRightCoord || $[1] !== scrollContainerElementRef || $[2] !== widths) {
    t1 = {
      coord: bottomRightCoord,
      scrollContainerElementRef,
      widths
    };
    $[0] = bottomRightCoord;
    $[1] = scrollContainerElementRef;
    $[2] = widths;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    anchorElement
  } = useFillHandleAnchorElement(t1);
  const isAnchorEditing = useIsAnchorCellEditing(anchorElement);
  if (!isCellSelectionEnabled(config)) {
    return null;
  }
  if (!hasSelection || isDragging || !anchorElement || isAnchorEditing) {
    return null;
  }
  let anchorInside;
  let t2;
  let t3;
  if ($[4] !== bottomRightCoord || $[5] !== columns || $[6] !== config || $[7] !== coords || $[8] !== copySelectionRef || $[9] !== getRowId || $[10] !== rows || $[11] !== track) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      var _config$actions;
      const cells = getSelectedCells(coords, columns, rows, getRowId);
      if (cells.length === 0) {
        t3 = null;
        break bb0;
      }
      const isNearRightEdge = bottomRightCoord != null && bottomRightCoord.columnIndex >= columns.length - EDGE_THRESHOLD;
      const isNearBottomEdge = bottomRightCoord != null && bottomRightCoord.rowIndex >= rows.length - EDGE_THRESHOLD;
      anchorInside = isNearRightEdge || isNearBottomEdge;
      let t4;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = I18n.text("datatable.cellSelection.copy");
        $[15] = t4;
      } else {
        t4 = $[15];
      }
      let t5;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = getCopyShortcutLabel();
        $[16] = t5;
      } else {
        t5 = $[16];
      }
      let t6;
      if ($[17] !== copySelectionRef) {
        t6 = {
          key: "copy",
          label: t4,
          Icon: CopyIcon,
          shortcut: t5,
          onClick: () => {
            var _copySelectionRef$cur;
            return (_copySelectionRef$cur = copySelectionRef.current) === null || _copySelectionRef$cur === void 0 ? void 0 : _copySelectionRef$cur.call(copySelectionRef, "click");
          }
        };
        $[17] = copySelectionRef;
        $[18] = t6;
      } else {
        t6 = $[18];
      }
      const copyItem = t6;
      const consumerItems = ((_config$actions = config.actions) !== null && _config$actions !== void 0 ? _config$actions : []).filter(action => action.isAvailable ? action.isAvailable(cells) : true).map(action_0 => ({
        key: action_0.key,
        label: action_0.label,
        Icon: action_0.icon,
        onClick: () => {
          track === null || track === void 0 || track({
            type: "cellSelectionActionClicked",
            actionKey: action_0.key,
            cellCount: cells.length,
            columnNames: Array.from(new Set(cells.map(_temp)))
          });
          action_0.onAction(cells);
        }
      }));
      t2 = [copyItem, ...consumerItems];
    }
    $[4] = bottomRightCoord;
    $[5] = columns;
    $[6] = config;
    $[7] = coords;
    $[8] = copySelectionRef;
    $[9] = getRowId;
    $[10] = rows;
    $[11] = track;
    $[12] = anchorInside;
    $[13] = t2;
    $[14] = t3;
  } else {
    anchorInside = $[12];
    t2 = $[13];
    t3 = $[14];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  const items = t2;
  let t4;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = I18n.text("datatable.cellSelection.actionsLabel");
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== items) {
    t5 = /*#__PURE__*/_jsx(CellActionMenu, {
      ariaLabel: t4,
      items: items
    });
    $[20] = items;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== anchorInside || $[23] !== t5) {
    t6 = /*#__PURE__*/_jsx(Overlay, {
      $inside: anchorInside,
      children: t5
    });
    $[22] = anchorInside;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== anchorElement || $[26] !== t6) {
    t7 = /*#__PURE__*/createPortal(t6, anchorElement);
    $[25] = anchorElement;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
};
function _temp(c) {
  return c.columnName;
}